import { Box, useMediaQuery, useTheme } from '@mui/material'
import { observer } from 'mobx-react-lite'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Sector,
} from 'recharts'
import { highlightChart } from '../../model/my-team-elearning'
import { Palette } from 'src/feature/theme/model/Palette'
import { Word } from 'src/feature/localization/component/atom/Word'
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded'

export type TeamLearningHighlightChartChartProps = {
  highlightChart: highlightChart[]
}

const pieChartColors = [
  '#1B3A4B',
  '#36BBAF',
  '#88B04B',
  '#27827A',
]

export const TeamLearningHighlightChartChart = observer(({ highlightChart }: TeamLearningHighlightChartChartProps) => {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery('(max-width:600px)')
  const isMediumScreen = useMediaQuery('(max-width:1200px)')
  const isIntermediateScreen = useMediaQuery('(min-width:1200px) and (max-width:1500px)')
  const isLargeScreen = useMediaQuery('(min-width:1500px)')

  // Calculate the maximum percentage in the dataset for normalization
  const maxPercentage = Math.max(...highlightChart.map(item => item.percentage || 0))

  // Create array of all indices to make all segments use the custom shape
  const allIndices = highlightChart.map((_, index) => index)

  // Custom render function with max percentage closure
  const renderNormalizedVariableRadiusShape = (props: any) => {
    const { cx, cy, innerRadius, startAngle, endAngle, fill, payload } = props
    
    // Calculate outer radius based on percentage - higher percentage = longer radius
    const baseRadius = 40 // Reduced base radius to fit container
    const maxAdditionalRadius = 50 // Reduced max additional radius
    const percentage = payload.percentage || 0
    
    // Normalize based on the highest percentage in the dataset to ensure full chart utilization
    const normalizedPercentage = maxPercentage > 0 ? (percentage / maxPercentage) * 100 : percentage
    const outerRadius = baseRadius + (normalizedPercentage / 100) * maxAdditionalRadius
    
    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
    )
  }

  return (
    <Box sx={styles.root}>
      {highlightChart.length > 0 && <ResponsiveContainer width='100%' height='100%'>
        <PieChart>
          <Pie
            data={highlightChart}
            dataKey='percentage'
            nameKey='courseName'
            cx='50%'
            cy='50%'
            outerRadius='100%'
            labelLine={false}
            activeIndex={allIndices}
            activeShape={renderNormalizedVariableRadiusShape}
            label={({ cx, cy, midAngle, innerRadius, percentage }) => {
              // Calculate label position based on the variable radius with normalization
              const RADIAN = Math.PI / 180
              const baseRadius = 40 // Match the shape's base radius
              const maxAdditionalRadius = 50 // Match the shape's max additional radius
              // Use the same normalization as the shape
              const normalizedPercentage = maxPercentage > 0 ? (percentage / maxPercentage) * 100 : percentage
              const outerRadius = baseRadius + (normalizedPercentage / 100) * maxAdditionalRadius
              const radius = innerRadius + (outerRadius - innerRadius) / 2
              const x = cx + radius * Math.cos(-midAngle * RADIAN)
              const y = cy + radius * Math.sin(-midAngle * RADIAN)
            
              return (
                <text
                  x={x}
                  y={y}
                  fill={Palette.WHITE}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize={11}
                >
                  {`${percentage}%`}
                </text>
              )
            }}
          >
            {highlightChart.map((entry, index) => (
              <Cell
                key={`cell-${entry.courseId}`}
                fill={pieChartColors[index % pieChartColors.length]}
                stroke='none'
              />
            ))}
          </Pie>
          <Legend
            layout={isSmallScreen ? 'horizontal' : 'vertical'}
            verticalAlign={isSmallScreen ? 'bottom' : 'middle'}
            align={isSmallScreen ? 'center' : 'right'}
            width={isLargeScreen ? 220 : isIntermediateScreen ? 120 : isMediumScreen ? 320 : isSmallScreen ? 300 : 220}
            wrapperStyle={{
              fontSize: 11,
              padding: 0,
              margin: 0,
            }}
            content={({ payload }) => (
              <ul
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                  gap: 5,
                  margin: 0,
                  padding: 0,
                }}
              >
                {payload?.map((entry, index) => (
                  <li key={`item-${entry.value}`} style={{ display: 'flex', alignItems: 'start', gap: 5, margin: 0, padding: 0 }}>
                    <Word sx={{ color: pieChartColors[index % pieChartColors.length] }}><CheckBoxRoundedIcon fontSize='small' /></Word>
                    <Word sx={{ color: theme.palette.text.secondary, fontSize: 11 }}>{entry.value}</Word>
                  </li>
                ))}
              </ul>
            )}
          />
        </PieChart>
      </ResponsiveContainer>}
    </Box>
  )
})

const styles = {
  root: {
    width: '100%',
    height: 250,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}
